/* iter55y — Hook + Provider for the writer word-count / badges state.
 *
 * The hook wraps the /api/wordcount/status endpoint. It:
 *   1. Fetches on mount (component render).
 *   2. Exposes a `refresh()` callable used after every chapter save.
 *   3. Fires a celebration toast when new badges are awarded.
 *   4. Surfaces the 10%-off Pro promo code once silver is earned.
 */
import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

const API = (process.env.REACT_APP_BACKEND_URL || '') + '/api';

const CELEBRATIONS = {
  silver:     { emoji: '🥈', headline: '20,000 words! Silver leaf earned.',   sub: 'You unlocked a 10%-off Pro upgrade for the next 30 days.' },
  gold:       { emoji: '🥇', headline: '40,000 words! Gold leaf earned.',     sub: 'You are two-thirds through a full novel manuscript.' },
  platinum:   { emoji: '💎', headline: '60,000 words! Platinum leaf earned.', sub: 'Publishable novel-length. Time to think about editors.' },
  multicolor: { emoji: '🏆', headline: 'Novel complete!',                     sub: 'The multicolor leaf is yours forever.' },
};

export function useWordcount() {
  const [state, setState] = useState({
    loading:         true,
    total_words:     0,
    per_project:     [],
    badges:          [],
    current_badge:   null,
    next_threshold:  null,
    prizes:          {},
    pro_promo_code:  null,
  });

  const refresh = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/wordcount/status`, { withCredentials: true });
      const d = r.data || {};
      // Fire a celebration toast for each newly-awarded badge.
      for (const b of (d.newly_awarded || [])) {
        const c = CELEBRATIONS[b.tier] || {};
        toast.success(`${c.emoji || '⭐'} ${c.headline || 'New badge earned!'}`, {
          description: c.sub || '',
          duration:    9000,
        });
      }
      setState({ ...d, loading: false });
      return d;
    } catch (err) {
      setState((s) => ({ ...s, loading: false }));
      return null;
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  // iter55y — Re-check the counter when the user returns to the tab.
  // Captures the common "wrote in another window, came back to Pro
  // Writer" flow without spamming polls. Also listens for the custom
  // `pw:chapter-saved` DOM event that chapter-save sites emit.
  useEffect(() => {
    const onFocus = () => refresh();
    const onSaved = () => refresh();
    window.addEventListener('focus', onFocus);
    window.addEventListener('pw:chapter-saved', onSaved);
    return () => {
      window.removeEventListener('focus', onFocus);
      window.removeEventListener('pw:chapter-saved', onSaved);
    };
  }, [refresh]);

  return { ...state, refresh };
}
